import React, {Fragment} from 'react';
import {Dialog, Tab, Transition} from '@headlessui/react';
import Icon from '@/shared/components/Icon';
import clsx from 'clsx';
import {PatientEditFormBody} from '@/modules/provider-console/components/PatientEditModal';
import ClinicalContextPanel from './ClinicalContextPanel';
import type {Test} from '@/shared/types/api';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** The order being reviewed — drives the Clinical tab (on_biotin /
     *  cycle_phase live per-order). */
    order: Test;
    /** Patient user ID for the Patient tab.  Threaded through from the
     *  route, since orders themselves don't carry users.id. */
    patientUserId: number | null;
    /** Which tab opens active.  Result-review flows default to 'clinical';
     *  generic "Edit Patient" entry points should pass 'patient'. */
    defaultTab?: 'patient' | 'clinical';
    /** Fired after a successful save on either tab so the host can
     *  refetch (re-computes ref ranges with updated demographics + clinical
     *  flags). */
    onSaved?: () => void;
}

const TAB_BASE =
    'flex-1 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px focus:outline-none';
const TAB_ACTIVE = 'border-mld-primary text-mld-primary';
const TAB_INACTIVE = 'border-transparent text-mld-muted-text hover:text-mld-menu-text hover:border-mld-panel-border';

/**
 * Combined "Patient + Clinical Context" modal.  Replaces the two
 * previously-separate modals (PatientEditModal opened by an Edit button
 * next to the page title, and ClinicalContextModal opened by a Clinical
 * Context button) — a provider reviewing a result usually wants to
 * adjust both kinds of context in the same flow.
 *
 * Tabs are mounted always so the queries inside each body kick off on
 * open (no lazy-mount lag when switching tabs).  Headless UI Tab keeps
 * focus management and ARIA wiring correct.
 */
const PatientContextModal: React.FC<Props> = ({
    isOpen,
    onClose,
    order,
    patientUserId,
    defaultTab = 'patient',
    onSaved,
}) => {
    const initialIndex = defaultTab === 'clinical' ? 1 : 0;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-start justify-center p-4 py-8">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-2xl rounded-2xl border border-mld-panel-border bg-mld-menu-background text-mld-menu-text text-left shadow-xl">
                                <Tab.Group defaultIndex={initialIndex}>
                                    <div className="flex items-center justify-between border-b border-mld-panel-border pr-4">
                                        <Tab.List className="flex flex-1 pl-6">
                                            <Tab
                                                className={({selected}) =>
                                                    clsx(TAB_BASE, selected ? TAB_ACTIVE : TAB_INACTIVE)
                                                }>
                                                Patient
                                            </Tab>
                                            <Tab
                                                className={({selected}) =>
                                                    clsx(TAB_BASE, selected ? TAB_ACTIVE : TAB_INACTIVE)
                                                }>
                                                Clinical Context
                                            </Tab>
                                        </Tab.List>
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            aria-label="Close"
                                            className="ml-3 rounded-md p-1 text-mld-muted-text hover:bg-mld-panel-border/30 hover:text-mld-menu-text transition-colors">
                                            <Icon name="xmark" className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <Tab.Panels className="px-6 py-5">
                                        <Tab.Panel>
                                            <PatientEditFormBody
                                                isOpen={isOpen}
                                                userId={patientUserId}
                                                onClose={onClose}
                                                onSaved={() => onSaved?.()}
                                            />
                                        </Tab.Panel>
                                        <Tab.Panel>
                                            <ClinicalContextPanel
                                                order={order}
                                                onSaved={onSaved}
                                            />
                                        </Tab.Panel>
                                    </Tab.Panels>
                                </Tab.Group>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default PatientContextModal;
